import { z } from 'zod';
import type { SuccessModeValue } from '~/modules/business-manager/forms/types';
import type {
    EnquiryDownloadLink,
    PublicEnquiryForm,
} from '../types/public-enquiry-form';
import type { PredictedDestination } from '../util/predict-enquiry-destination';

/**
 * Where the server routed an enquiry. `kind` is the form destination it
 * resolved to (Inbox, Teammate, Calendar, Message, Branch).
 */
export interface RoutedDestination {
    kind: string;
    /** Set when the enquiry routed to a meeting session. */
    calendarSessionId: number | null;
    /** Set when the enquiry was handed to a branch. */
    branchId: number | null;
}

/**
 * The routing and after-submit results `createEnquiry` reports beyond the
 * enquiry itself. Every value is null when the API does not report it; the
 * client then falls back to its own prediction and the form's settings.
 */
export interface EnquirySubmitOutcome {
    routedDestination: RoutedDestination | null;
    successMode: SuccessModeValue | null;
    successMessage: string | null;
}

export const EMPTY_SUBMIT_OUTCOME: EnquirySubmitOutcome = {
    routedDestination: null,
    successMode: null,
    successMessage: null,
};

const optionalId = z.coerce.number().int().positive().nullish().catch(null);

const GRAPHQL_OUTCOME = z.object({
    RoutedDestination: z
        .object({
            Kind: z.string(),
            CalendarSessionID: optionalId,
            BranchID: optionalId,
        })
        .nullish()
        .catch(null),
    SuccessMode: z
        .enum(['Message', 'Redirect', 'Downloads'])
        .nullish()
        .catch(null),
    SuccessMessage: z.string().nullish().catch(null),
});

/**
 * Reads the outcome fields off a `CreateEnquiryResult`. Parsed rather than typed
 * off the generated mutation so a result that does not select them, or a
 * malformed one, reads as "not reported" instead of breaking the submit.
 */
export function readEnquirySubmitOutcome(
    result: unknown,
): EnquirySubmitOutcome {
    const parsed = GRAPHQL_OUTCOME.safeParse(result);
    if (!parsed.success) return EMPTY_SUBMIT_OUTCOME;
    const {
        RoutedDestination: routed,
        SuccessMode,
        SuccessMessage,
    } = parsed.data;
    return {
        routedDestination: routed
            ? {
                  kind: routed.Kind,
                  calendarSessionId: routed.CalendarSessionID ?? null,
                  branchId: routed.BranchID ?? null,
              }
            : null,
        successMode: SuccessMode ?? null,
        successMessage: SuccessMessage?.trim() || null,
    };
}

/** What a decline says when the rule has no message of its own. */
export const DEFAULT_DECLINED_MESSAGE =
    'This form is not taking enquiries like this one.';

/** The part of the submit route's JSON this module reads. */
interface SubmitResponseShape {
    success?: boolean;
    error?: boolean;
    declined?: boolean;
    declinedMessage?: string | null;
    message?: string | null;
    outcome?: EnquirySubmitOutcome;
    data?: {
        __typename?: string;
        Enquiries?: { ID: number | string }[] | null;
        Downloads?:
            | {
                  ID: number;
                  Title: string;
                  Url: string;
                  ExpiresAt: string;
                  FileName?: string | null;
                  FileSize: number;
                  IsExternal: boolean;
              }[]
            | null;
    } | null;
}

const asShape = (data: unknown): SubmitResponseShape | null =>
    data && typeof data === 'object' ? (data as SubmitResponseShape) : null;

const createEnquiryResult = (data: unknown) => {
    const shape = asShape(data);
    return shape?.success && shape.data?.__typename === 'CreateEnquiryResult'
        ? shape.data
        : null;
};

/** The id of the enquiry a successful submit created, or null. */
export function readCreatedEnquiryId(data: unknown): number | null {
    const parsed = Number(createEnquiryResult(data)?.Enquiries?.[0]?.ID);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/** Signed links the submission earned, when the form offers downloads. */
export function readOfferedDownloads(data: unknown): EnquiryDownloadLink[] {
    return (createEnquiryResult(data)?.Downloads ?? []).map((download) => ({
        id: download.ID,
        title: download.Title,
        url: download.Url,
        expiresAt: download.ExpiresAt,
        fileName: download.FileName ?? null,
        fileSize: download.FileSize,
        isExternal: download.IsExternal,
    }));
}

/** The outcome the submit route attached to a successful response. */
export function readSubmitOutcome(data: unknown): EnquirySubmitOutcome {
    const shape = asShape(data);
    return shape?.success && shape.outcome
        ? shape.outcome
        : EMPTY_SUBMIT_OUTCOME;
}

/**
 * The message of a submit a routing rule declined, when the route returned the
 * decline as data; null for anything else.
 */
export function readDeclinedMessage(data: unknown): string | null {
    const shape = asShape(data);
    if (!shape?.error || !shape.declined) return null;
    return shape.declinedMessage?.trim() || DEFAULT_DECLINED_MESSAGE;
}

/**
 * What a failed submit says went wrong, when the route returned it as data (a
 * website embed's failures, or a decline the form shows itself); null when the
 * failure was flashed as a toast or carries no message.
 */
export function readSubmitErrorMessage(data: unknown): string | null {
    const declined = readDeclinedMessage(data);
    if (declined !== null) return declined;
    const shape = asShape(data);
    if (!shape?.error) return null;
    return shape.message?.trim() || null;
}

/** What the enquiry gate shows once a submit has landed. */
export type EnquiryGateOutcome =
    | {
          kind: 'calendar';
          sessionId: number;
          enquiryId: number | null;
          downloads: EnquiryDownloadLink[];
      }
    | {
          kind: 'downloads';
          downloads: EnquiryDownloadLink[];
          message: string | null;
      }
    | { kind: 'success'; message: string | null };

/**
 * The step after a successful send. The server's routed destination is
 * authoritative; only when it does not report one does the client's own
 * prediction decide whether a meeting is offered.
 */
export function resolveEnquiryGateOutcome({
    data,
    predicted,
    form,
}: {
    /** The submit route's response. */
    data: unknown;
    /** The client's routing prediction at submit time, if any. */
    predicted: PredictedDestination | null;
    form: PublicEnquiryForm | null;
}): EnquiryGateOutcome {
    const outcome = readSubmitOutcome(data);
    const downloads = readOfferedDownloads(data);
    const message =
        outcome.successMessage ?? (form?.successMessage.trim() || null);
    const routed = outcome.routedDestination;
    const sessionId = routed
        ? routed.kind === 'Calendar'
            ? routed.calendarSessionId
            : null
        : predicted?.destination === 'Calendar'
          ? predicted.calendarSessionId
          : null;
    if (sessionId) {
        return {
            kind: 'calendar',
            sessionId,
            enquiryId: readCreatedEnquiryId(data),
            downloads,
        };
    }
    if (downloads.length > 0) {
        return { kind: 'downloads', downloads, message };
    }
    return { kind: 'success', message };
}
